import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { SCORECARD_SECTION_ID } from '../utils/constants';

function BreedScorecardSection({ className, sections }) {
  return (
    <>
      {Object.entries(sections).map(([title, items], idx, arr) => {
        const filteredItems = items.filter(item => item.description !== null);
        return (
          <div
            key={idx}
            className={classnames(
              'BreedScorecardSection mb10',
              {
                'mb0-md': idx === arr.length - 1
              },
              className
            )}
          >
            <h5 className="f4 lh-title mb4">{title}</h5>
            <ul className="font-16">
              {filteredItems.map((item, i) => (
                <li key={i} className="flex items-center pv3 bb b--black-10">
                  <p className="flex-auto flex-basis-0 fw-medium pr4">
                    {item.name}
                  </p>
                  {item.description && (
                    <p className="flex-auto flex-basis-0">{item.description}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

BreedScorecardSection.propTypes = {
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  sections: PropTypes.object.isRequired
};

BreedScorecardSection.defaultProps = {
  className: null
};

export default function BreedScorecard() {
  const {
    breedData: { scorecard: scorecardData }
  } = useSelector(({ server }) => server);

  const firstColumnData = {
    'Characteristics and temperament': [
      {
        name: 'Affectionate with family',
        description: scorecardData.affectionate_with_family
      },
      { name: 'Watchdog level', description: scorecardData.watchdog_level },
      { name: 'Playfulness', description: scorecardData.playfulness },
      { name: 'Adaptability', description: scorecardData.adaptability },
      { name: 'Social needs', description: scorecardData.social_needs },
      { name: 'Temperament', description: scorecardData.temperament },
      { name: 'Intelligence', description: scorecardData.intelligence },
      {
        name: 'Good with other dogs',
        description: scorecardData.good_with_dogs
      },
      {
        name: 'Good with cats or other pets',
        description: scorecardData.good_with_pets
      },
      {
        name: 'Friendly with strangers',
        description: scorecardData.good_with_strangers
      },
      {
        name: 'Good as a service dog',
        description: scorecardData.good_service_dog
      },
      {
        name: 'Good for apartments',
        description: scorecardData.good_for_apartments
      },
      { name: 'Barking level', description: scorecardData.barking_level }
    ],
    Appearance: [
      { name: 'Height', description: scorecardData.height },
      { name: 'Size', description: scorecardData.size },
      { name: 'Colors', description: scorecardData.colors },
      { name: 'Coat texture', description: scorecardData.coat_texture },
      { name: 'Coat type', description: scorecardData.coat_type },
      { name: 'Coat length', description: scorecardData.coat_length }
    ],
    Training: [
      { name: 'Trainability', description: scorecardData.trainability }
    ]
  };

  const secondColumnData = {
    Exercise: [
      { name: 'Exercise needs', description: scorecardData.exercise_needs },
      { name: 'Exercise time', description: scorecardData.exercise_time },
      {
        name: 'Mental exercise needs',
        description: scorecardData.mental_exercise_needs
      },
      {
        name: 'Favorite activities',
        description: scorecardData.favorite_activities
      }
    ],
    Grooming: [
      { name: 'Grooming needs', description: scorecardData.grooming_needs },
      {
        name: 'Brushing frequency',
        description: scorecardData.brushing_frequency
      },
      {
        name: 'Needs professional grooming?',
        description: scorecardData.needs_professional_grooming
      },
      { name: 'Drooling level', description: scorecardData.drooling_level }
    ],
    'Health issues': [
      ...scorecardData.health_issues.map(issue => {
        return { name: issue };
      })
    ],
    Other: [
      { name: 'Bred for', description: scorecardData.bred_for },
      {
        name: 'Country of origin',
        description: scorecardData.country_of_origin
      },
      { name: 'Popularity level', description: scorecardData.popularity_level }
    ]
  };

  return (
    <div
      id={SCORECARD_SECTION_ID}
      className="BreedScorecard row bg-grayish-blue ph5 pt12 ph20-md"
    >
      <div className="col-12">
        <h3 className="mb4 mb12-md f6 f8-md lh-title">Breed Scorecard</h3>
        <div className="row no-gutters">
          <div className="col-12 col-md-6">
            <BreedScorecardSection
              className="pr14-md"
              sections={firstColumnData}
            />
          </div>
          <div className="col-12 col-md-6">
            <BreedScorecardSection sections={secondColumnData} />
          </div>
        </div>
      </div>
    </div>
  );
}
